import { useState } from 'react';
import { MoreHorizontal, Archive, ArchiveRestore, Languages } from 'lucide-react';
import { palette, admin, fontFamily, transition } from '../../../styles/tokens';
import type { Conversation } from '../types';

interface Props {
  conversation: Conversation;
  showTranslations: boolean;
  onToggleTranslations: () => void;
  onShelve: () => void;
  onUnshelve: () => void;
}

const GRADIENTS = [
  ['#6CB4EE', '#4A90D9'], ['#12B76A', '#0E9255'], ['#F59E0B', '#D97706'],
  ['#8B5CF6', '#7C3AED'], ['#EC4899', '#DB2777'], ['#14B8A6', '#0D9488'],
  ['#F97316', '#EA580C'],
];
function nameGradient(name: string): string[] {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return GRADIENTS[Math.abs(h) % GRADIENTS.length];
}
function initials(name: string): string {
  const parts = name.split(' ');
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function ThreadHeader({ conversation: c, showTranslations, onToggleTranslations, onShelve, onUnshelve }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [g1, g2] = nameGradient(c.otherParticipantName);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 16px', borderBottom: `1px solid ${admin.border}`,
      background: palette.white, flexShrink: 0,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 18, flexShrink: 0,
        background: `linear-gradient(135deg, ${g1}, ${g2})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 600, color: '#fff',
      }}>
        {initials(c.otherParticipantName)}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800, fontFamily }}>
          {c.otherParticipantName}
        </div>
        {c.otherParticipantRole && (
          <div style={{ fontSize: 11, color: palette.g500, textTransform: 'capitalize' }}>
            {c.otherParticipantRole}
          </div>
        )}
      </div>

      {/* Translation toggle with badge */}
      <button
        onClick={onToggleTranslations}
        title={showTranslations ? 'Show originals' : 'Show translations'}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          height: 32, borderRadius: 6, border: `1px solid ${admin.border}`,
          background: showTranslations ? palette.accentWash : palette.white,
          cursor: 'pointer', padding: '0 10px',
          transition: `background ${transition.fast}`,
        }}
      >
        <Languages size={14} color={showTranslations ? palette.accent : palette.g500} />
        {showTranslations && (
          <span style={{ fontSize: 11, fontWeight: 500, color: palette.accent }}>On</span>
        )}
      </button>

      {/* More menu */}
      <div style={{ position: 'relative' }}>
        <button onClick={() => setMenuOpen(!menuOpen)} style={iconBtn}>
          <MoreHorizontal size={14} color={palette.g500} />
        </button>
        {menuOpen && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            background: palette.white, borderRadius: 10, border: `1px solid ${admin.border}`,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)', minWidth: 180, zIndex: 50,
            overflow: 'hidden',
          }}>
            <button onClick={() => { if (c.shelved) onUnshelve(); else onShelve(); setMenuOpen(false); }} style={menuItem}>
              {c.shelved ? <ArchiveRestore size={14} /> : <Archive size={14} />}
              {c.shelved ? 'Reopen conversation' : 'Shelve conversation'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  width: 32, height: 32, borderRadius: 6, border: `1px solid ${admin.border}`,
  background: palette.white, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const menuItem: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', padding: '10px 14px', border: 'none',
  background: 'transparent', fontSize: 13, color: palette.g700,
  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
};
